import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logOut } from "@/features/auth/authSlice";
import { broadcastAuthChange } from "@/features/auth/authSync";
import { getBaseUrl, getDevJwtToken } from "@/services/api/baseUrl";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers) => {
    const devToken = getDevJwtToken();
    if (devToken) {
      headers.set("authorization", `Bearer ${devToken}`);
    }
    headers.set("Accept", "*/*");
    return headers;
  },
});

/** Matches intellimorph-tools: a 401 ends the session, but only when one exists. */
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (
    result?.meta?.response?.status === 401 &&
    !getDevJwtToken() &&
    api.getState()?.auth?.user
  ) {
    api.dispatch(logOut());
    broadcastAuthChange("loggedOut");
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Invoices",
    "MyInvoices",
    "StarredInvoices",
    "InvoiceDetail",
    "InvoiceFile",
    "InvoiceInsights",
    "MyInvoiceInsights",
    "InvoiceCategories",
    "InvoiceCreationLogs",
    "ProcessingStatuses",
    "ReviewStatuses",
    "ReviewTypes",
    "ApprovalRules",
    "ApprovalRulesFields",
    "ApprovalRequests",
    "Comments",
    "Audits",
    "Users",
    "UserSettings",
    "CompanyConfiguration",
    "AuthStatus",
  ],
  endpoints: () => ({}),
});

/**
 * Tags invalidated whenever an invoice changes in a way that can move it between
 * queues or change the dashboard counts. Kept in one place so a new mutation
 * cannot silently forget one of them.
 */
export const INVOICE_LIST_TAGS = [
  { type: "Invoices", id: "LIST" },
  { type: "MyInvoices", id: "LIST" },
  { type: "StarredInvoices", id: "LIST" },
  { type: "ApprovalRequests", id: "LIST" },
  { type: "InvoiceInsights", id: "ALL" },
  { type: "MyInvoiceInsights", id: "ALL" },
];

export function invoiceMutationTags(guid) {
  return [
    ...INVOICE_LIST_TAGS,
    { type: "InvoiceDetail", id: guid },
    { type: "Audits", id: guid },
  ];
}
